export type ExpireUnit = "second" | "minute" | "hour" | "day";

export interface StorageSetOptions {
  version?: number;
  expire?: { value: number; unit: ExpireUnit };
}

interface StorageEnvelope<T> {
  v: number;
  expiresAt?: number;
  data: T;
}

const UNIT_MS: Record<ExpireUnit, number> = {
  second: 1_000,
  minute: 60_000,
  hour: 3_600_000,
  day: 86_400_000,
};

function isEnvelope(value: unknown): value is StorageEnvelope<unknown> {
  return (
    typeof value === "object" &&
    value !== null &&
    "v" in value &&
    "data" in value &&
    typeof (value as StorageEnvelope<unknown>).v === "number"
  );
}

/**
 * Read a value from localStorage. Returns `null` and removes the key if
 * the stored entry is missing, unparseable, version-mismatched, or expired.
 */
export function storageGet<T>(key: string, version: number = 1): T | null {
  try {
    const raw = localStorage.getItem(key);
    if (raw === null) return null;

    const parsed: unknown = JSON.parse(raw);
    if (!isEnvelope(parsed)) {
      localStorage.removeItem(key);
      return null;
    }

    if (parsed.v !== version) {
      localStorage.removeItem(key);
      return null;
    }

    if (parsed.expiresAt !== undefined && Date.now() > parsed.expiresAt) {
      localStorage.removeItem(key);
      return null;
    }

    return parsed.data as T;
  } catch {
    localStorage.removeItem(key);
    return null;
  }
}

/**
 * Write a value to localStorage wrapped in a versioned envelope.
 * Optionally set an expiration via `options.expire`.
 */
export function storageSet<T>(
  key: string,
  data: T,
  options?: StorageSetOptions,
): void {
  const version = options?.version ?? 1;
  const envelope: StorageEnvelope<T> = { v: version, data };

  if (options?.expire) {
    const ms = options.expire.value * UNIT_MS[options.expire.unit];
    envelope.expiresAt = Date.now() + ms;
  }

  localStorage.setItem(key, JSON.stringify(envelope));
}

/** Remove a key from localStorage. */
export function storageRemove(key: string): void {
  localStorage.removeItem(key);
}

